import React, { useState } from 'react';
import cx from 'classnames';
import { uniq } from 'lodash';
import { Col, Collapse, Grid, Row } from 'react-bootstrap';

import { ModalProvider, useModalContext } from '@magoosh/context/modal_context';
import { DefaultButton, SecondaryButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from "@magoosh/lib/icons";
import { paths } from 'config/path_helpers';
import { formatPrice } from 'utilities/money';
import { formatTimeRange, longTimeZoneName } from 'utilities/time';

// import { Cohort } from '@magoosh/pages/payment_flows/types';
import Confirmation from './confirmation';
import { AddOnOffer } from '@payments/types';

import Derrik from './assets/gmat_live_classes/derrick.png';
import Erika from './assets/gmat_live_classes/erika.png';
import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

const CLASS_DETAILS = [
  {
    description: "Review key facts about GMAT structure, timing, and scoring; learn test-taking strategies for getting the most points on each section; dig into each GMAT question type; and discuss how to study effectively.",
    name: "Test Strategy"
  },
  {
    description: "Review major Arithmetic topics, explore how these topics are tested on the GMAT, and learn and practice GMAT Math strategies.",
    name: "Arithmetic"
  },
  {
    description: "Explore how to approach GMAT Reading Comprehension questions, practice skimming passages, and learn strategies for each Reading Comprehension question type.",
    name: "Reading Comprehension"
  },
  {
    description: "Review major Algebra topics, explore how these topics are tested on the GMAT, and continue learning and practicing GMAT Math strategies.",
    name: "Algebra"
  },
  {
    description: "Explore how to approach GMAT Critical Reasoning questions, practice breaking down arguments, and learn strategies for each Critical Reasoning question type.",
    name: "Critical Reasoning"
  },
  {
    description: "Review major Geometry topics, explore how these topics are tested on the GMAT, and continue learning and practicing GMAT Math strategies.",
    name: "Geometry"
  },
  {
    description: "Explore how to approach GMAT Sentence Correction questions, learn strategies for identifying errors in sentences, and review major grammatical concepts.",
    name: "Sentence Correction"
  },
  {
    description: "Dig into some of the lesser-known Math concepts that you're likely to see on test day, explore how these topics are tested on the GMAT, and continue learning and practicing GMAT Math strategies.",
    name: "Advanced Math"
  }
];


const LiveClassesAddOn: React.FC<AddOnOffer & { compact?: boolean }> = (props) => {
  const [expanded, setExpanded] = useState(!props.compact);
  const { discountedPrice, fullPrice } = props.price;

  const { setModal } = useModalContext();

  const handleAddOnPurchaseClicked = () => {
    if (props.paymentDetails) {
      setModal({
        header: 'Add Live Classes',
        body: (
          <Confirmation
            addOnName="Live Classes"
            cohortId={props.orderItem.purchasable.cohortId}
            handleClose={() => setModal(null)}
            successMessage="Navigate to the Classes tab on your Magoosh dashboard to access each class and its associated materials."
            {...props} />
        )
      });
    } else {
      window.location.href = `${paths.subscriptions(props.orderItem.purchasable.planSlug)}?coupon=${props.price.couponCode}`;
    }
  };

  return (
    <div className={cx(style.container, 'd-flex flex-column', { 'u-padding-T-m u-padding-B-xl': !props.compact })}>
      {
        !props.compact && (
          <div>
            <h4 className="text-gray">One-Time Offer</h4>
            <h2 className="u-margin-T-n">
              Add On-Demand Classes for{' '}
              {
                discountedPrice !== fullPrice && (
                  <>
                    <span style={{textDecoration: 'line-through'}}>
                      {formatPrice(fullPrice, 'usd', true)}
                    </span>
                    {' '}
                  </>
                )
              }
              {formatPrice(discountedPrice, 'usd', true)}!
            </h2>

            <hr className="u-margin-T-n" />
          </div>
        )
      }

      <div>
        <p>
          Get extra support and accountability in engaging, on-demand classes directed directed by one of our 99th-percentile instructors.
        </p>

        {
          discountedPrice !== fullPrice && (
            <p>
              <strong>Plus - if you sign up now, you’ll get a big discount</strong> (regular price
              is {formatPrice(fullPrice, 'usd', true)})!
            </p>
          )
        }

        <div className={cx('d-flex u-margin-V-l', style.ctas)}>
          <SecondaryButton
            href={props.paymentDetails ? null : props.orderUrl}
            onClick={props.paymentDetails ? handleAddOnPurchaseClicked : null}
            target="_blank"
          >
            Yes, Add <span className="hidden-xs">On-Demand Classes</span> for {formatPrice(discountedPrice, 'usd', true)}
          </SecondaryButton>

          {props.nextPath && <DefaultButton className="u-margin-L-s" href={props.nextPath}>
            No Thanks
          </DefaultButton>}
        </div>

        <div className="border-radius-all u-margin-V-l u-padding-A-m" style={{backgroundColor: '#F5EEFC'}}>
          <p className="text-uppercase"><strong>16 HOURS OF ON-DEMAND INSTRUCTION</strong></p>

          {
            CLASS_DETAILS.map((classDetail, idx) => (
              <ClassDetail key={idx} classNumber={idx + 1} {...classDetail} />
            ))
          }
        </div>

        {!expanded && <a className="text-gray-light" onClick={() => setExpanded(true)}>See More</a>}

        {
          expanded && (
            <>
              <div className="u-margin-T-xl">
                <div className="u-margin-B-s">
                  Each on-demand class features an engaging expert instructor, targeted homework assignments, and more! Learn more about Classes <a href="/about-live-classes">here</a>.
                </div>
              </div>

              <div className="d-flex u-margin-T-xl">
                <div>
                  <span style={{color: colors.statusYellow}}>
                    <FontAwesomeIcon icon="star" />
                    <FontAwesomeIcon icon="star" />
                    <FontAwesomeIcon icon="star" />
                    <FontAwesomeIcon icon="star" />
                    <FontAwesomeIcon icon="star" />
                  </span>

                  <p>
                    "[The instructor] always brings such great energy and encouragement. The tips and tricks are truly helpful."
                  </p>
                  <p>
                    - GMAT Student, October 2021
                  </p>
                </div>
              </div>

              <div className="u-margin-T-xl">
                <div className="u-margin-B-s">
                  <strong><u>Meet Your Instructors</u></strong>
                </div>

                <div className="d-flex justify-content-center">
                  <div>
                    <img
                      alt="Brittny Photo"
                      src={Derrik}
                      style={{objectFit: 'cover'}} />
                  </div>

                  <div>
                    <img
                      alt="Linnea Photo"
                      src={Erika}
                      style={{objectFit: 'cover'}} />
                  </div>
                </div>
              </div>
            </>
          )
        }
      </div>
    </div>
  );
};

interface ClassDetailProps {
  classNumber: number;
  description: string;
  name: string;
}

const ClassDetail: React.FC<ClassDetailProps> = ({ classNumber, description, name }) => {
  const [isExpanded, setIsExpanded] = React.useState(classNumber === 1);

  return (
    <div className="u-margin-T-s">
      <div onClick={() => setIsExpanded(!isExpanded)} style={{cursor: 'pointer'}}>
        <strong>
          <span className="u-margin-R-xs" style={{color: colors.brandFuchsia}}>
            <span className="u-margin-R-xxs">
              <FontAwesomeIcon icon={isExpanded ? 'minus' : 'plus'} />
            </span>
            <span>
              Class {classNumber}.
            </span>
          </span>
          <span>
            {name}
          </span>
        </strong>
      </div>

      <Collapse in={isExpanded}>
        <div>{description}</div>
      </Collapse>
    </div>
  );
};

const LiveClassesAddOnWithProvider: React.FC<AddOnOffer> = (props) => {
  return (
    <ModalProvider>
      <LiveClassesAddOn {...props} />
    </ModalProvider>
  );
};

export default LiveClassesAddOnWithProvider;
